import { MSC_TERMS } from './termsData';
// src/pages/Booking/GuestBookingResult.js
// 상세 예약 결과를 표시하는 화면 컴포넌트. 결제 완료 여부에 따라
// 문구, 공유 버튼, 결제 내역 등을 동적으로 렌더링한다.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../css/Booking/guestBookingResult.css';
import { ReactComponent as DotIcon } from '../../assets/icons/svg/dot.svg';
import { ReactComponent as IndentIcon } from '../../assets/icons/svg/indentSymbol.svg';
import { resolveCautionByLineId } from '../../utils/cautionData';
import { extractOnboardCreditSummaryFromSource, normalizeOnboardCreditSummary } from '../../utils/onboardCredit';
import { apiGet } from '../../utils/api';
import { PromotionDiscountRows } from '../../utils/promotionSummary';
import { withMscBellissimaCancelNotice } from '../../utils/mscBellissimaCancelNotice';
import PassengerEnglishName from '../../components/Common-components/PassengerEnglishName';

const DEFAULT_PASSENGER_LABELS = {
  adults: '성인',
  adult: '성인',
  children: '소아',
  child: '소아',
  teens: '소아',
  infants: '영유아',
};

const PASSENGER_TYPE_ORDER = ['adults', 'children'];

const mapPaxTypeToCategory = (type = '') => {
  const normalized = String(type || '').toLowerCase();
  if (['adult', 'adults', '성인'].includes(normalized)) return 'adults';
  return 'children';
};

const normalizePassengerGender = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['m', 'male', 'mr'].includes(normalized)) return 'Male';
  if (['f', 'female', 'ms', 'mrs', 'miss'].includes(normalized)) return 'Female';
  return value || '';
};

const buildPassengerDob = (passenger = {}) => {
  if (passenger?.dob) return passenger.dob;

  const year = String(passenger?.birth_year || '').trim();
  const month = String(passenger?.birth_month || '').trim();
  const day = String(passenger?.birth_day || '').trim();
  if (!year || !month || !day) return '';

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const buildPassengerPriceItemsFromDetail = (passenger = {}) => {
  const items = [
    {
      description: '선실 요금',
      category: 'fare',
      price: Number(passenger?.cabin_price ?? 0),
      symbol: '$',
    },
    {
      description: '세금',
      category: 'tax',
      price: Number(passenger?.tax_price ?? 0),
      symbol: '$',
    },
    {
      description: '선상팁',
      category: 'tip',
      price: Number(passenger?.tip_price ?? 0),
      symbol: '$',
    },
  ];

  return items.filter(item => Number.isFinite(item.price) && item.price !== 0);
};

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const MSC_LINE_ID = 16;
const NORWEGIAN_LINE_ID = 17;
const ROYAL_CARIBBEAN_LINE_ID = 22;
const DISNEY_LINE_ID = 73;
const RESULT_DETAIL_PATH_MAP = {
  '-1': 'cancellation-history',
  '0': 'reservation-complete',
  '1': 'partial-payment',
  '2': 'payment-complete',
  '3': 'boarding-complete',
  '5': 'external-reservation',
};

const DEFAULT_TERMS = [
  {
    title: "특별 약관",
    description: "본 상품의 취소는 국외여행 표준약관 제16조 외에 약관 제6조에 의해 '국외여행 특별약관'이 적용됩니다. 특별약관은 표준약관보다 높은 취소료가 부과될 수 있으니 세부기준을 반드시 확인하셔야 합니다.",
    items: [
      "취소료는 크루즈 선사 규정을 따르며 예외사항이 없습니다.",
      "예약금 이상의 취소료가 발생하는 시점부터는 차액을 추가로 납부하셔야 합니다."
    ],
    extra: {
      type: "link",
      text: "약관 상세 보기 >",
      url: "#"
    }
  },
  {
    title: "취소료 규정",
    description: "",
    items: [
      "출항~ 91일 전 취소 시(예약일~2월 28일): 위약금 없음",
      "출항 90일 ~ 61일 전 취소 시(3월 1일~3월 30일): 여행경비의 30%",
      "출항 60일 ~ 31일 전 취소 시(3월 31일~4월 29일): 여행경비의 50%",
      "출항 30일 ~ 출발 당일 취소 시(4월 30일 이후): 여행경비의 100%"
    ],
    extra: {
      type: "notes",
      notes: [
        "*취소 접수는 업무시간 내에 유선 접수만 가능합니다.",
        "*접수시간: 월~금 9:30~15:00, 토/일요일 공휴일 제외"
      ]
    }
  }
];


const NORWEGIAN_TERMS = [
  {
    title: "특별 약관",
    description: "※ 노르웨이지안 크루즈는 일반 해외여행시 적용되는 국외여행표준약관이 아닌 특별 크루즈 약관에 따라 취소 규정이 적용됩니다. 이 규정은 노르웨이지안 크루즈 이용시 전세계에 공통 적용되는 규정입니다. 이에 따른 취소료 규정을 꼭 확인해주시기 바랍니다.",
    items: [
      "취소료는 크루즈 선사 규정을 따르며 예외사항이 없습니다.",
      "예약금 이상의 취소료가 발생하는 시점부터는 차액을 추가로 납부하셔야 합니다."
    ]
  },
  {
    title: "취소료 규정",
    description: "",
    items: [
      { type: "label", text: "[취소료 규정]" },
      "90일 전 : 무료",
      "89일 - 75 일 전 : 예약금 (총 요금의 20%)",
      "74일 - 50일 전 : 총 요금의 25%",
      "49일 - 29일 전 : 총 요금의 50%",
      "28일 - 15일 전 : 총 요금의 75%",
      "14일 - 출발일 : 총 요금의 100%",
      { type: "note", text: "※ BOGOFREE 프로모션(1번째 승객 요금 결제 시 2번째 승객은 무료, 단 세금은 별도 부담)의 경우, 지불된 예약금은 환불이 불가능합니다. 할인된 예약금(Reduced Deposit) 프로모션은 스위트(Suite) 및 헤이븐(Haven) 카테고리에는 적용되지 않습니다. 따라서 스위트 또는 헤이븐으로 업그레이드할 경우, 예약금 전액을 즉시 납부해야 합니다." },
      { type: "note", text: "※ 예약 확정 후 모든 변경 요청은 취소료 부과 이전 시점에만 가능하며, 취소료 부과 시점 이후에는 모든 예약 변경이 불가능합니다." },
      { type: "note", text: "※ 선실 요금의 25%, 50% 시점에 부과되는 취소료가 예약금보다 낮을 경우, 예약금이 취소료로 부과됩니다." },
      { type: "note", text: "※ 취소 요청 접수 시간은 평일 09:30 ~ 15:00(토, 공휴일 휴무)입니다. 접수시간 이후 취소 요청은 다음 영업일 기준으로 취소 처리가 진행 되며, 이에 따른 취소료가 적용됩니다." }
    ]
  }
];

const ROYAL_CARIBBEAN_TERMS = [
  {
    title: "특별 약관",
    description: "※ 로얄캐리비안 크루즈는 일반 해외여행시 적용되는 국외여행표준약관이 아닌 특별 크루즈 약관에 따라 취소 규정이 적용됩니다. 이 규정은 로얄캐리비안 크루즈 이용시 전세계에 공통 적용되는 규정입니다. 이에 따른 취소료 규정을 꼭 확인해주시기 바랍니다.",
    items: [
      "취소료는 크루즈 선사 규정을 따르며 예외사항이 없습니다.",
      "예약금 이상의 취소료가 발생하는 시점부터는 차액을 추가로 납부하셔야 합니다."
    ]
  },
  {
    title: "취소료 규정",
    description: "",
    items: [
      { type: "note", text: "로얄캐리비안 크루즈의 취소 규정은 크루즈 출항일과 일정 길이에 따라 다르게 적용됩니다." },
      { type: "note", text: "예약금은 상품 및 프로모션 조건에 따라 환불 불가(NRD)로 적용될 수 있습니다." },
      { type: "note", text: "예약 변경 또는 취소를 원하실 경우, 아래 규정을 반드시 확인해주시기 바랍니다." },
      { type: "label", text: "[1- 5박 일정]" },
      "75일 전: 무료 (단, 예약금 환불불가 조건은 1인당 USD 150)",
      "74 - 60일 : 예약금",
      "59 - 30일 : 총 요금의 50%",
      "29 - 15일 : 총 요금의 75%",
      "14 - 출발 당일 : 총 요금의 100%",
      { type: "label", text: "[6박 - 9박 일정]" },
      "90일 전 : 무료 (단, 예약금 환불불가 조건은 1인당 USD 350)",
      "89 - 75일 : 예약금",
      "74 - 45일 : 총 요금의 50%",
      "44 - 15일 : 총 요금의 75%",
      "14 - 출발 당일 : 총 요금의 100%",
      { type: "label", text: "[10박 - 14박 일정]" },
      "90일 전 : 무료 (단, 예약금 환불불가 조건은 1인당 USD 500)",
      "89 - 75일 : 예약금",
      "74 - 61일 : 총 요금의 50%",
      "60 - 31일 : 총 요금의 75%",
      "30 - 출발 당일 : 총 요금의 100%",
      { type: "label", text: "[15박 이상 일정]" },
      "121일 전 : 무료 (단, 예약금 환불불가 조건은 1인당 USD 500)",
      "120 - 61일 : 총 요금의 25%",
      "60 - 41일 : 총 요금의 50%",
      "40 - 25일 : 총 요금의 75%",
      "24 - 출발 당일 : 총 요금의 100%",
      { type: "label", text: "[예약금 안내]" },
      { type: "note", text: "*1~5박: 1인당 USD 150" },
      { type: "note", text: "*6~9박: 1인당 USD 350" },
      { type: "note", text: "*10박 이상: 1인당 USD 500" },
      { type: "note", text: "*스위트: 크루즈 요금의 10% 또는 위 금액 중 큰 금액" },
      { type: "note", text: "*아이콘호 STAR 등급: 1인당 USD 600" },
      { type: "note", text: "※ 예약금 환불 불가(NRD) 조건으로 예약하신 경우, 결제하신 예약금은 환불되지 않습니다." },
      { type: "note", text: "※ 선실 요금의 25%, 50% 시점에 부과되는 취소료가 예약금보다 낮을 경우, 예약금 금액이 취소료로 부과됩니다." },
      { type: "note", text: "※ 취소 요청 접수 시간은 평일 09:30 ~ 15:00(토, 공휴일 휴무)입니다. 접수시간 이후 취소 요청은 다음 영업일 기준으로 취소 처리가 진행 되며, 이에 따른 취소료가 적용됩니다." }
    ]
  }
];

const DISNEY_TERMS = [
  {
    title: "특별 약관",
    description: "※ 디즈니 크루즈는 일반 해외여행시 적용되는 국외여행표준약관이 아닌 특별 크루즈 약관에 따라 취소 규정이 적용됩니다. 이 규정은 디즈니 크루즈 이용시 전세계에 공통 적용되는 규정입니다. 이에 따른 취소료 규정을 꼭 확인해주시기 바랍니다.",
    items: [
      "취소료는 크루즈 선사 규정을 따르며 예외사항이 없습니다.",
      "예약금 이상의 취소료가 발생하는 시점부터는 차액을 추가로 납부하셔야 합니다."
    ]
  },
  {
    title: "취소료 규정",
    description: "",
    items: [
      { type: "label", text: "[1- 5박 일정]" },
      { type: "label", text: "*내측 / 오션뷰 / 베란다" },
      "예약일 - 90일 전 : 무료",
      "89일 - 45일 전 : 예약금 (총 요금의 10%)",
      "44일 - 30일 전 : 총 요금의 50%",
      "29 - 15일 전 : 총 요금의 75%",
      "14 - 출발일 : 총 요금의 100%",
      { type: "label", text: "*컨시어지 / 스위트" },
      "예약일 - 45일 전 : 예약금 (총 요금의 10%)",
      "44일 - 30일 전 : 총 요금의 50%",
      "29 - 15일 전 : 총 요금의 75%",
      "14 - 출발일 : 총 요금의 100%",
      { type: "label", text: "[6박 이상 일정]" },
      { type: "label", text: "*내측 / 오션뷰 / 베란다" },
      "예약일 - 120일 전 : 무료",
      "119일 - 56일 전 : 예약금 (총 요금의 10%)",
      "55일 - 30일 전 : 총 요금의 50%",
      "29 - 15일 전 : 총 요금의 75%",
      "14 - 출발일 : 총 요금의 100%",
      { type: "label", text: "*컨시어지 / 스위트" },
      "예약일 - 56일 전 : 예약금 (총 요금의 10%)",
      "55일 - 30일 전 : 총 요금의 50%",
      "29 - 15일 전 : 총 요금의 75%",
      "14 - 출발일 : 총 요금의 100%",
      { type: "note", text: "※ 인사이드 개런티, 오션뷰 개런티, 베란다 개런티 등급으로 예약 시, 크루즈 요금은 100% 환불 불가합니다." },
      { type: "note", text: "예약 확정을 위한 예약금은 크루즈 요금의 10%입니다." },
      { type: "note", text: "※ 예약 후 모든 변경이 불가능하며, 예약 규정에 따라 취소료가 발생합니다." },
      { type: "note", text: "※ 취소 요청 접수 시간은 평일 09:30 ~ 15:00(토, 공휴일 휴무)입니다. 접수시간 이후 취소 요청은 다음 영업일 기준으로 취소 처리가 진행 되며, 이에 따른 취소료가 적용됩니다." }
    ]
  }
];

const resolveTermsByLineId = (lineId, context = {}, options = {}) => {
  const numericLineId = Number(lineId);
  if (numericLineId === MSC_LINE_ID) {
    return withMscBellissimaCancelNotice(MSC_TERMS, { ...context, lineId: numericLineId }, options);
  }
  if (numericLineId === NORWEGIAN_LINE_ID) return NORWEGIAN_TERMS;
  if (numericLineId === ROYAL_CARIBBEAN_LINE_ID) return ROYAL_CARIBBEAN_TERMS;
  if (numericLineId === DISNEY_LINE_ID) return DISNEY_TERMS;
  return DEFAULT_TERMS;
};

export default function GuestBookingResult({ bookingPayload, ageConfig }) {
  const navigate = useNavigate(); // 라우팅 이동을 담당하는 훅
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 949); // 반응형 테이블 전환 기준
  const [resolvedPaymentData, setResolvedPaymentData] = useState(null);
  const adultLabel = ageConfig?.adult?.summaryLabel ?? DEFAULT_PASSENGER_LABELS.adults;
  const childLabel = ageConfig?.child?.summaryLabel ?? DEFAULT_PASSENGER_LABELS.children;
  const passengerLabels = useMemo(() => ({
    adults: adultLabel,
    adult: adultLabel,
    children: childLabel,
    child: childLabel,
    teens: childLabel,
    infants: DEFAULT_PASSENGER_LABELS.infants,
  }), [adultLabel, childLabel]);

  useEffect(() => {
    // 뷰포트 변경에 따라 테이블 구조를 전환하기 위해 resize 이벤트를 감지한다.
    function handleResize() {
      setIsTablet(window.innerWidth <= 949);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { reservationType = false } = bookingPayload; // true: 결제 완료, false: 예약 요청 상태

  // 홈으로 이동
  const handleGoHome = () => {
    navigate('/');
  };
  // 마이페이지 이동 함수 - 추후 결제 유무 에 따라서 type 구분
  const handleGoMypage = () => {
    navigate('/mypage');
  };

  // API 응답 구조에서 화면에 필요한 데이터만 미리 구조분해한다.
  const { detailData } = bookingPayload;
  const { formDataInfo, bookingResult = {}, basketData = {} } = bookingPayload.passengerInfo || {};
  const paymentResult = bookingPayload?.paymentResult || {};
  const detailSource = resolvedPaymentData || paymentResult || {};
  const traveltekResponse = bookingResult.traveltek_response || {};
  const bookingResults = Array.isArray(traveltekResponse.results)
    ? traveltekResponse.results
    : Array.isArray(bookingResult.results)
      ? bookingResult.results
      : [];
  const bookingResData = bookingResults[0] || {};

  const basketResults = Array.isArray(basketData.results) ? basketData.results : [];
  const basketResData = basketResults[0];

  const passengersFromDetails = Array.isArray(bookingResData?.bookingdetails?.passengers)
    ? bookingResData.bookingdetails.passengers
    : [];
  const passengersFromItems = Array.isArray(bookingResData?.bookingdetails?.bookingitems?.[0]?.passengers)
    ? bookingResData.bookingdetails.bookingitems[0].passengers
    : [];
  const passengersFromApi = Array.isArray(detailSource?.pax_list)
    ? detailSource.pax_list.map((passenger, index) => ({
      paxno: index + 1,
      pax_type: passenger?.pax_type || '',
      paxtype: passenger?.pax_type || '',
      lastname: passenger?.last_name || '',
      firstname: passenger?.first_name || '',
      dob: buildPassengerDob(passenger),
      gender: passenger?.gender || '',
      phone: passenger?.phone || '',
      cabin_price: passenger?.cabin_price,
      tax_price: passenger?.tax_price,
      tip_price: passenger?.tip_price,
    }))
    : [];
  const passengers = passengersFromApi.length > 0
    ? passengersFromApi
    : (passengersFromDetails.length > 0 ? passengersFromDetails : passengersFromItems);
  const passengerCountSummary = useMemo(() => {
    const base = passengers.reduce((acc, cur) => {
      const category = mapPaxTypeToCategory(cur?.paxtype || cur?.type || '');
      if (!category) return acc;
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
    // 승객 정보가 없으면 formData나 counts 기반으로 보정
    if (Object.keys(base).length === 0) {
      const rawCounts = bookingPayload?.counts || {};
      if (rawCounts.adults) base.adults = rawCounts.adults;
      if (rawCounts.children) base.children = rawCounts.children;
    }
    return base;
  }, [passengers, bookingPayload?.counts]);
  const tonnage = detailData?.schedule?.tonnage; // 선박 무게

  const bookingDetails = bookingResData?.bookingdetails || {};
  const primaryBookingItem = bookingDetails?.bookingitems?.[0] || {};
  const cruiseDetail = primaryBookingItem?.cruisedetail || {};
  const lineId = Number(
    detailSource?.line_id ??
      bookingPayload?.detailData?.schedule?.line_id ??
      bookingPayload?.detailData?.schedule?.lineid ??
      bookingPayload?.bookingResult?.line_id ??
      bookingPayload?.bookingResult?.lineid ??
      bookingResult?.line_id ??
      cruiseDetail?.line_id ??
      cruiseDetail?.lineid ??
      0
  );
  const isMscLine = lineId === MSC_LINE_ID;
  // 금액: totalcost를 우선 사용, 없으면 totalprice/totaldue/sellprice 순
  const totalPrice = bookingDetails.totalcost ?? bookingDetails.totalprice ?? bookingDetails.totaldue ?? cruiseDetail.sellprice ?? 0;
  const sailDate =
    detailSource?.sail_date ||
    cruiseDetail.saildate ||
    cruiseDetail.startdate ||
    primaryBookingItem?.startdate ||
    '';
  const detailItinerary =
    cruiseDetail?.itinerary ||
    basketResData?.basketitems?.[0]?.cruisedetail?.itinerary ||
    [];

  const sortedItinerary = useMemo(() => {
    if (!Array.isArray(detailItinerary)) return [];
    return [...detailItinerary].sort((a, b) => {
      const dayA = typeof a?.day === 'number' ? a.day : Number(a?.day || 0);
      const dayB = typeof b?.day === 'number' ? b.day : Number(b?.day || 0);
      return dayA - dayB;
    });
  }, [detailItinerary]);

  // 일정 기반으로 기간 계산: 일정 개수-1 = 박, 일정 개수 = 일
  const itineraryLength = Array.isArray(sortedItinerary) ? sortedItinerary.length : 0;
  const sailNightsFromItinerary = itineraryLength > 0 ? Math.max(itineraryLength - 1, 0) : null;
  const sailNightsRaw = cruiseDetail.sailnights ?? cruiseDetail.duration ?? sailNightsFromItinerary;
  const sailNights = sailNightsRaw != null ? Number(sailNightsRaw) : 0; // 기간
  const shipName = detailSource?.ship_name || cruiseDetail.shipname; // 선박이름
  const cruiseName = cruiseDetail.cruisename; // 크루즈 이름
  const cabins = primaryBookingItem?.cruisedetail?.cabins;
  const cabinObj = Array.isArray(cabins) && cabins.length > 0
    ? cabins[0]
    : (primaryBookingItem?.cruisedetail?.cabin || {}); // 선실 정보
  const cabinNo = detailSource?.cabin_number || primaryBookingItem?.cruisedetail?.cabinno || cabinObj?.number || ''; //  선실 번호
  const carbinGradeInfo = bookingPayload?.carbinGradeInfo;
  const translatedCabinName =
    detailSource?.cabin_type ||
    carbinGradeInfo?.grade_name_ko ||
    carbinGradeInfo?.grade_name ||
    cabinObj?.cabinname ||
    cabinObj?.cabintype ||
    '';
  const cabinCode = cabinObj?.cabingrade || carbinGradeInfo?.cabincode || '';
  const cabinPosition = cabinObj?.position;

  const passengersPrice = basketResData?.basketitems?.[0]?.cruisedetail?.perperson || []; // 상세 가격정보

  const bookingReference =
    bookingDetails.bookingreference ||
    primaryBookingItem?.cruisedetail?.bookingreference ||
    '';
  const reservationIdx =
    detailSource?.reservation_idx ||
    bookingResult?.reservation_idx ||
    bookingPayload?.reservation_idx ||
    '';
  const reservationCode =
    bookingResult?.reservation_code ||
    bookingPayload?.paymentResult?.reservation_code ||
    bookingPayload?.reservation_code ||
    '';
  const normalizedReservationIdx = Number.parseInt(String(reservationIdx || '').trim(), 10);
  const dinnerTimeDesc =
    detailSource?.dinner_time ||
    bookingResult?.dinner_time ||
    bookingPayload?.passengerInfo?.confirm?.dinner_time ||
    '';
  const formatReservationDate = (dateString) => {
    if (!dateString) return null;
    const dateObj = new Date(dateString);
    if (Number.isNaN(dateObj.getTime())) return null;
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    const weekday = WEEKDAYS[dateObj.getDay()];
    const hour = String(dateObj.getHours()).padStart(2, '0');
    const minute = String(dateObj.getMinutes()).padStart(2, '0');
    return `${year}년 ${month}월 ${day}일 (${weekday}) ${hour}:${minute}`;
  };

  const receiptList = Array.isArray(bookingDetails?.receipts) ? bookingDetails.receipts : [];
  const primaryReceipt = receiptList[0] || null;

  const reservationCreateDate =
    detailSource?.create_date ||
    detailSource?.reservation_create_date ||
    bookingResult?.reservation_create_date ||
    bookingPayload?.reservation_create_date ||
    '';

  const bookingDateRaw =
    primaryReceipt?.created ||
    bookingDetails.datebooked ||
    bookingDetails.datebookedutc ||
    bookingDetails.transactiondateutc ||
    reservationCreateDate ||
    bookingResData?.transactiondateutc ||
    '';
  const bookingDateFormatted = formatReservationDate(bookingDateRaw);
  const reservationNumberLabel = reservationCode || bookingReference || '확인 중';
  const reservationDateLabel = bookingDateFormatted || '-';
  const scheduleIdx =
    detailData?.schedule?.schedule_idx ||
    detailData?.schedule?.idx ||
    detailData?.schedule?.id ||
    bookingPayload?.detailData?.schedule?.schedule_idx ||
    bookingPayload?.detailData?.schedule?.idx ||
    bookingPayload?.detailData?.schedule?.id ||
    '';

  const normalizeStatusCode = (value) => {
    const normalized = String(value ?? '').trim();
    if (Object.prototype.hasOwnProperty.call(RESULT_DETAIL_PATH_MAP, normalized)) {
      return normalized;
    }
    return null;
  };

  const reservationStatusCode =
    normalizeStatusCode(detailSource?.reservation_status) ||
    normalizeStatusCode(detailSource?.status) ||
    normalizeStatusCode(bookingResult?.status) ||
    normalizeStatusCode(bookingPayload?.status) ||
    (reservationType ? '2' : '0');
  const reservationDetailPath = reservationStatusCode === '2'
    ? 'payment-complete'
    : (RESULT_DETAIL_PATH_MAP[reservationStatusCode] || 'reservation-complete');
  const canGoReservationDetail = Number.isFinite(normalizedReservationIdx) && normalizedReservationIdx > 0;

  useEffect(() => {
    let cancelled = false;

    const hasPaymentSummary = Boolean(paymentResult?.payment_summary || bookingResult?.payment_summary);

    if (!reservationType || !canGoReservationDetail || hasPaymentSummary) {
      setResolvedPaymentData(null);
      return undefined;
    }

    (async () => {
      try {
        const detailResponse = await apiGet('/reservation/get_reservation_detail.php', {
          idx: normalizedReservationIdx,
        });
        if (!cancelled && detailResponse?.success === true) {
          setResolvedPaymentData(detailResponse);
        }
      } catch (error) {
        console.error('Failed to resolve payment detail for booking result page:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    reservationType,
    canGoReservationDetail,
    normalizedReservationIdx,
    paymentResult?.payment_summary,
    bookingResult?.payment_summary,
  ]);

  const handleGoReservationDetail = () => {
    if (!canGoReservationDetail) {
      handleGoMypage();
      return;
    }

    const nextState = {
      reservationSchIdx: scheduleIdx || undefined,
      reservationCode: reservationNumberLabel || undefined,
    };
    if (reservationStatusCode === '5') {
      nextState.is_external_reservation = true;
    }

    navigate(
      `/mypage/${reservationDetailPath}/detail?status=${reservationStatusCode}&idx=${encodeURIComponent(normalizedReservationIdx)}`,
      { state: nextState }
    );
  };

  const formatCurrency = (value, currency = 'USD') => {
    const numeric = Number(value) || 0;
    return numeric.toLocaleString('en-US', { style: 'currency', currency });
  };
  const parseAmount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatDateLabel = (dateString) => {
    if (!dateString) return null;
    const dateObj = new Date(dateString);
    if (Number.isNaN(dateObj.getTime())) return null;
    return `${dateObj.getFullYear()}년 ${String(dateObj.getMonth() + 1).padStart(2, '0')}월 ${String(dateObj.getDate()).padStart(2, '0')}일`;
  };

  const paymentsArray = Array.isArray(bookingDetails?.payments) ? bookingDetails.payments : [];
  const paymentCurrency = paymentsArray[0]?.currency || 'USD';
  const paymentDataSource = detailSource;

  const legacyPaymentSummary = useMemo(() => {
    const total = Number(bookingDetails.totalcost ?? bookingDetails.totalprice ?? bookingDetails.totaldue ?? cruiseDetail.sellprice ?? 0) || 0;
    let paidTotal = 0;
    let latestPayment = null;
    let dueDateCandidate = null;

    paymentsArray.forEach((payment) => {
      if (!payment || typeof payment !== 'object') return;
      const paidAmount = Number(payment.paidamount ?? payment.costamount ?? payment.totalcharge ?? 0);
      if (paidAmount > 0) {
        paidTotal += paidAmount;
        const paidOn = payment.paidon || payment.datebooked || payment.createdon || '';
        const latestPaidOn = latestPayment ? (latestPayment.paidon || latestPayment.datebooked || latestPayment.createdon || '') : '';
        if (!latestPayment || (paidOn && new Date(paidOn) >= new Date(latestPaidOn))) {
          latestPayment = payment;
        }
      }
      if (!dueDateCandidate && payment?.duedate) {
        dueDateCandidate = payment.duedate;
      }
    });

    const balance = Math.max(total - paidTotal, 0);
    const dueDate =
      balance > 0
        ? dueDateCandidate ||
          bookingDetails?.bookingitems?.[0]?.cruisedetail?.startdate ||
          bookingDetails?.bookingitems?.[0]?.startdate ||
          bookingDetails?.startdate ||
          null
        : null;

    const sanitizeCardLabel = (payment) => {
      if (!payment) return null;
      const cardDigits = typeof payment.cardnumber === 'string' ? payment.cardnumber.replace(/[^0-9]/g, '') : '';
      const lastFour = cardDigits.slice(-4);
      const cardType = payment.cardtype || payment.paymentmethod || '';
      if (!lastFour && !cardType) return null;
      if (!lastFour) return cardType;
      return `${cardType ? `${cardType} ` : ''}****${lastFour}`;
    };

    const depositMeta = latestPayment
      ? {
          date: latestPayment.paidon || latestPayment.datebooked || latestPayment.createdon || '',
          method: latestPayment.paymentmethod || latestPayment.cardtype || '',
          cardLabel: sanitizeCardLabel(latestPayment),
        }
      : null;

    return {
      total,
      paidTotal,
      balance,
      dueDate,
      depositMeta,
    };
  }, [bookingDetails, cruiseDetail, paymentsArray]);
  const legacyPaymentReceipts = useMemo(() => {
    if (!Array.isArray(paymentsArray) || paymentsArray.length === 0) return [];

    const totalAmount = parseAmount(bookingDetails.totalcost ?? bookingDetails.totalprice ?? bookingDetails.totaldue ?? cruiseDetail.sellprice) ?? 0;
    const depositAmount = parseAmount(bookingDetails.totaldeposit) ?? 0;

    return paymentsArray
      .map((payment) => {
        if (!payment || typeof payment !== 'object') return null;

        const amount = parseAmount(payment.paidamount ?? payment.costamount ?? payment.totalcharge);
        if (amount === null || amount <= 0) return null;

        const paidAt =
          payment.paidon ||
          payment.realpaidon ||
          payment.datebooked ||
          payment.createdon ||
          '';
        let paymentType = 'T';
        if (depositAmount > 0 && amount <= (depositAmount + 0.01) && amount < (totalAmount - 0.01)) {
          paymentType = 'D';
        } else if (depositAmount > 0 && totalAmount > depositAmount && amount < (totalAmount - 0.01)) {
          paymentType = 'F';
        }

        return {
          paid_at: paidAt || '',
          paid_date: paidAt ? String(paidAt).slice(0, 10) : '',
          payment_amount_usd: String(amount),
          payment_type: paymentType,
          card_type: payment.cardtype || payment.paymentmethod || '',
          card_number_masked: '',
        };
      })
      .filter(Boolean);
  }, [paymentsArray, bookingDetails, cruiseDetail]);
  // API 요약이 있으면 그 값을 사용하고, 없으면 기존 계산값으로 보정
  const apiPaymentSummary = paymentDataSource?.payment_summary || bookingResult?.payment_summary || null;
  const hasApiPaymentSummary = Boolean(apiPaymentSummary);
  const paymentReceipts = Array.isArray(paymentDataSource?.payment_receipts) && paymentDataSource.payment_receipts.length > 0
    ? paymentDataSource.payment_receipts
    : (Array.isArray(bookingResult?.payment_receipts) && bookingResult.payment_receipts.length > 0
      ? bookingResult.payment_receipts
      : (hasApiPaymentSummary ? [] : legacyPaymentReceipts));
  const receiptPaidTotal = paymentReceipts.reduce((sum, receipt) => {
    const amountVal = parseAmount(receipt?.payment_amount_usd);
    return amountVal !== null ? sum + amountVal : sum;
  }, 0);
  const totalAmountFromApi = parseAmount(apiPaymentSummary?.total_amount);
  const depositAmountFromApi = parseAmount(apiPaymentSummary?.deposit_amount);
  const finalAmountFromApi = parseAmount(apiPaymentSummary?.final_amount);
  const totalAmountDisplay = totalAmountFromApi !== null ? totalAmountFromApi : Number(totalPrice || 0);
  const depositAmountDisplay = depositAmountFromApi !== null
    ? depositAmountFromApi
    : Number(bookingDetails.totaldeposit ?? 0);
  const finalAmountDisplay = finalAmountFromApi !== null
    ? finalAmountFromApi
    : Math.max(totalAmountDisplay - depositAmountDisplay, 0);
  const paidTotalDisplay = receiptPaidTotal > 0 ? receiptPaidTotal : legacyPaymentSummary.paidTotal;
  const depositPaid = apiPaymentSummary
    ? apiPaymentSummary.deposit_paid === true
    : (depositAmountDisplay <= 0 || paidTotalDisplay >= (depositAmountDisplay - 0.01));
  const finalPaid = apiPaymentSummary
    ? apiPaymentSummary.final_paid === true
    : (finalAmountDisplay <= 0 || paidTotalDisplay >= (depositAmountDisplay + finalAmountDisplay - 0.01));
  const depositDueDate = apiPaymentSummary?.deposit_due_date || null;
  const finalDueDate = apiPaymentSummary?.final_due_date || null;
  const depositStatusLabel = depositAmountDisplay > 0
    ? (depositPaid ? '결제완료' : (depositDueDate ? `${String(depositDueDate).slice(0, 10)}까지` : '-'))
    : '';
  const finalStatusLabel = finalAmountDisplay > 0
    ? (finalPaid ? '결제완료' : (finalDueDate ? `${String(finalDueDate).slice(0, 10)}까지` : '-'))
    : '';

  // 결제 내역 표시용 보정:
  // - 예약금/잔금 모두 결제완료이고 영수증 합계가 총합보다 적게 찍혀 있을 때
  //   마지막 잔금(F) 또는 전액(T) 영수증의 표시 금액을 총합에 맞게 가산한다.
  const paymentReceiptsForView = React.useMemo(() => {
    const list = Array.isArray(paymentReceipts) ? paymentReceipts : [];
    if (!isMscLine || !(depositPaid && finalPaid) || list.length === 0) return list;

    const diff = totalAmountDisplay - paidTotalDisplay;
    if (!(Number.isFinite(diff) && diff > 0.009)) return list; // 1센트 이하 무시

    const findLastIndexByType = (types) => {
      for (let i = list.length - 1; i >= 0; i--) {
        const t = String(list[i]?.payment_type || '').toUpperCase();
        if (types.includes(t)) return i;
      }
      return -1;
    };

    let targetIdx = findLastIndexByType(['F']);
    if (targetIdx === -1) targetIdx = findLastIndexByType(['T']);
    if (targetIdx === -1) targetIdx = list.length - 1;
    if (targetIdx < 0) return list;

    const cloned = list.map(r => ({ ...r }));
    const base = parseAmount(cloned[targetIdx]?.payment_amount_usd) || 0;
    const adjusted = base + diff;
    cloned[targetIdx].payment_amount_usd_adjusted = adjusted;
    return cloned;
  }, [isMscLine, paymentReceipts, depositPaid, finalPaid, totalAmountDisplay, paidTotalDisplay]);
  const visiblePaymentReceiptsForView = React.useMemo(() => (
    paymentReceiptsForView.filter((receipt) => {
      const amountVal = parseAmount(receipt?.payment_amount_usd_adjusted ?? receipt?.payment_amount_usd);
      return amountVal !== null && Math.abs(amountVal) > 0.009;
    })
  ), [paymentReceiptsForView]);

  const onboardCreditSummary = useMemo(() => {
    const normalized = normalizeOnboardCreditSummary(
      paymentDataSource?.onboard_credit || bookingResult?.onboard_credit
    );
    if (normalized) return normalized;

    const basketCruiseDetail = basketResData?.basketitems?.[0]?.cruisedetail;
    return extractOnboardCreditSummaryFromSource(basketCruiseDetail)
      || extractOnboardCreditSummaryFromSource(cruiseDetail);
  }, [
    basketResData,
    paymentDataSource,
    bookingResult?.onboard_credit,
    cruiseDetail,
  ]);

  if (!bookingResData || !basketResData) {
    return (
      <div className="booking-result">
        <p>예약 상세 정보를 불러오지 못했습니다. 이전 단계로 돌아가 다시 시도해주세요.</p>
        <button type="button" className="btn" onClick={handleGoHome}>홈으로 이동</button>
      </div>
    );
  }

  const formatPhoneDisplay = (rawValue, fallbackValue = '') => {
    const target = (rawValue || fallbackValue || '').trim();
    if (!target) return '';

    if (target.startsWith('+')) {
      const segments = target.split(/[-\s]+/).filter(Boolean);
      const dialSegment = segments.shift() || '';
      const localSegments = segments.map(seg => seg.replace(/[^\d]/g, '')).filter(Boolean);
      if (localSegments.length > 0 && !localSegments[0].startsWith('0')) {
        localSegments[0] = `0${localSegments[0]}`;
      }
      const local = localSegments.join('-');
      const dial = dialSegment.startsWith('+') ? dialSegment : `+${dialSegment.replace(/^\+/, '')}`;
      return `(${dial}) ${local}`.trim();
    }

    const digits = target.replace(/[^\d]/g, '');
    if (!digits) return target;
    if (digits.length === 11) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return digits.replace(/(\d{3})(?=\d)/g, '$1-');
  };

  const primaryPassenger =
    passengers.find(p => String(p?.paxno) === '1') ||
    passengers[0] ||
    (Array.isArray(formDataInfo?.passengers) ? formDataInfo.passengers[0] : null);
  const primaryPhoneRaw =
    primaryPassenger?.telephone ||
    primaryPassenger?.phone ||
    formDataInfo?.contact?.telephone ||
    formDataInfo?.contact?.phone ||
    detailSource?.contact_phone ||
    '';
  const primaryPhone = formatPhoneDisplay(primaryPhoneRaw);

  // 공통 시간 포맷터: 유효한 시간만 HH:mm 형태로 반환하고 00:00:00은 숨김
  const formatTime = (time) => (time && time !== '00:00:00' ? time.slice(0, 5) : null);

  const formatDateWithWeekday = (dateString) => {
    if (!dateString) return null;
    const dateObj = new Date(dateString);
    if (Number.isNaN(dateObj.getTime())) return null;
    return `${dateObj.getFullYear()}년 ${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일(${WEEKDAYS[dateObj.getDay()]})`;
  };

  // 항구 정보를 통일된 서식(한글 + 영문)으로 만들어 출발/도착 요약과 상세 리스트에서 재사용
  const getPortInfo = (item) => {
    if (!item) return null;
    const baseName = item.itineraryname || item.name || '';
    if (!item.uniqueportid && !baseName) return '해상 (CRUISE)';
    if (!item.uniqueportid) return baseName || '해상 (CRUISE)';
    const nameKor = baseName;
    const nameEng = typeof baseName === 'string' && baseName.length > 0
      ? `(${baseName.toUpperCase()})`
      : '';
    return `${nameKor}${nameEng ? ` ${nameEng}` : ''}`.trim() || null;
  };

  // 출발·도착 요약 문자열을 생성해 상단 카드에서 재사용
  const formatScheduleLabel = (item, type) => {
    if (!item) return null;
    const isDeparture = type === 'departure';
    const dateSource = isDeparture ? (item.departdate || item.arrivedate) : (item.arrivedate || item.departdate);
    const timeSource = isDeparture ? item.departtime : item.arrivetime;
    const formattedDate = formatDateWithWeekday(dateSource);
    const formattedTime = formatTime(timeSource);
    const base = formattedDate
      ? `${formattedDate}${formattedTime ? ` ${formattedTime}` : ''}`
      : formattedTime;
    const portInfo = getPortInfo(item);
    if (!base && !portInfo) return null;
    return `${base || ''}${portInfo ? `${base ? ', ' : ''}${portInfo}` : ''}`;
  };

  const departureLabel = formatScheduleLabel(sortedItinerary[0], 'departure')
    || [sailDate, detailSource?.departure_port].filter(Boolean).join(' / ');
  const arrivalLabel = formatScheduleLabel(sortedItinerary[sortedItinerary.length - 1], 'arrival')
    || [detailSource?.end_date, detailSource?.arrival_port].filter(Boolean).join(' / ');
  const itinerarySummaryText =
    sortedItinerary
      .filter(item => item.uniqueportid || item.itineraryname || item.name)
      .map(item => item.itineraryname || item.name || '해상 (CRUISE)')
      .join(' ＞ ')
    || [detailSource?.departure_port, detailSource?.arrival_port].filter(Boolean).join(' ＞ ')
    || '-';
  const shipNameLabel = shipName
    ? (tonnage ? `${shipName} (${tonnage}톤)` : shipName)
    : '-';
  const cabinLabel = [
    translatedCabinName && cabinCode ? `${translatedCabinName} (${cabinCode})` : translatedCabinName || cabinCode,
    cabinNo,
    cabinPosition,
  ].filter(Boolean).join(' ') || '-';
  const cautionItems = resolveCautionByLineId(lineId);
  const termsSections = resolveTermsByLineId(lineId, {
    shipId: detailSource?.ship_code || detailSource?.ship_id || cruiseDetail?.shipid || cruiseDetail?.ship?.id,
    sailDate,
    departurePort: detailSource?.departure_port || cruiseDetail?.departureport || cruiseDetail?.startport,
    arrivalPort: detailSource?.arrival_port || cruiseDetail?.arrivalport || cruiseDetail?.endport,
    reservationDate: reservationCreateDate,
  }, { requireReservationDate: true });
  const specialTerms = termsSections[0] || DEFAULT_TERMS[0];
  const cancelTerms = termsSections[1] || DEFAULT_TERMS[1];

  const normalizeCautionItem = (item) =>
    typeof item === 'string' ? { text: item } : (item || { text: '' });

  const renderCautionSubItems = (items = [], depth = 1) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const listClassName = `caution-sublist caution-sublist-depth-${depth}`;

    return (
      <ul className={listClassName}>
        {items.map((rawItem, index) => {
          const normalized = normalizeCautionItem(rawItem);
          const subItems = Array.isArray(normalized.subItems) ? normalized.subItems : [];
          const itemClassName = `caution-subitem caution-subitem-depth-${depth}${normalized.type === 'note' ? ' caution-subitem-note' : ''}`;

          if (normalized.type === 'note') {
            return (
              <li key={`${depth}-${index}`} className={itemClassName}>
                <div className='caution-subnote'>{normalized.text}</div>
              </li>
            );
          }

          return (
            <li key={`${depth}-${index}`} className={itemClassName}>
              <div className='caution-subline'>
                <span className='caution-subdash'>-</span>
                <span>{normalized.text}</span>
              </div>
              {renderCautionSubItems(subItems, depth + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderCautionItems = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
      return <ul className='caution-contents' />;
    }

    return (
      <ul className='caution-contents'>
        {items.map((item, index) => {
          const normalized = normalizeCautionItem(item);
          const subItems = Array.isArray(normalized.subItems) ? normalized.subItems : [];

          return (
            <li key={`caution-${index}`} className='caution-item'>
              <DotIcon className='icon dot' />
              <div className='caution-text'>
                <div>{normalized.text}</div>
                {renderCautionSubItems(subItems, 1)}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderTermItems = (items = [], listClassName) => (
    <ul className={listClassName}>
      {items.map((item, index) => {
        const isObject = typeof item === 'object' && item !== null;
        const text = isObject ? item.text : item;
        const type = isObject ? (item.type || 'item') : 'item';
        const itemClassName =
          type === 'label'
            ? 'term-item-label'
            : type === 'note'
              ? 'term-item-note'
              : 'term-item';

        return (
          <li key={`${type}-${index}`} className={itemClassName}>
            {type === 'item' && <DotIcon className='icon dot' />}
            <span style={item?.emphasis ? { color: '#d32f2f' } : undefined}>{text}</span>
          </li>
        );
      })}
    </ul>
  );

  // 예약 결과 본문 렌더링
  return (
    <>
      <div className='result-title'>
        <h2 className='point-primary'>
          <span className='point-blue'>
            {(() => {
              const pax = primaryPassenger;
              const first = pax?.firstname || '';
              const last = pax?.lastname || '';
              const name = `${first} ${last}`.trim();
              return name || '홍길동';
            })()}
          </span>
          {reservationType ? '님, 예약이 완료되었습니다.' : '님, 예약이 요청되었습니다.'}
        </h2>
        {
          reservationType === true ? 
          '' :
          <p className='point-secondary mt-8'>*예약 요청 후 와우크루즈 고객센터를 통해<br/>예약금을 결제기한까지 납부해 주셔야 예약이 확정됩니다.</p>
        }
      </div>
      <div className="booking-result">
        {/* 예약 정보 테이블 */}
        <div className="table-wrapper">
          <div className='wrapper-title'>
            <h2>예약{reservationType === true ? "" : " 요청"} 정보</h2>
            {canGoReservationDetail ? (
              <button type='button' className='point-secondary' onClick={handleGoReservationDetail}>자세히 보기</button>
            ) : null}
          </div>

          <table className="reservation-info-table">
            <tbody>
              {reservationType === true && (
                isTablet ? (
                  <>
                    <tr>
                      <th>예약번호</th>
                      <td>{reservationNumberLabel}</td>
                    </tr>
                    <tr>
                      <th>예약 일시</th>
                      <td>{reservationDateLabel}</td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <th>예약번호</th>
                    <td>{reservationNumberLabel}</td>
                    <th>예약 일시</th>
                    <td>{reservationDateLabel}</td>
                  </tr>
                )
              )}

              {isTablet ? (
                <>
                  <tr>
                    <th>항차명</th>
                    <td>
                      {sailDate} 출발/ {cruiseName} / {sailNights}박 {sailNights + 1}일 / {shipName}
                    </td>
                  </tr>
                  <tr>
                    <th>정찬시간</th>
                    <td>{dinnerTimeDesc || '-'}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <th>항차명</th>
                  <td>
                    {sailDate} 출발/ {cruiseName} / {sailNights}박 {sailNights + 1}일 / {shipName}
                  </td>
                  <th>정찬시간</th>
                  <td>{dinnerTimeDesc || '-'}</td>
                </tr>
              )}
              {
                isTablet ? 
                <>
                  <tr>
                    <th>크루즈선</th>
                    <td>{shipNameLabel}</td>
                  </tr>
                  <tr>
                    <th>선실</th>
                    <td>{cabinLabel}</td>
                  </tr>                                
                </> : 
                <tr>
                  <th>크루즈선</th>
                  <td>{shipNameLabel}</td>
                  <th>선실</th>
                  <td>{cabinLabel}</td>
                </tr>
              }
              
              {isTablet ? 
              <>
                <tr>
                  <th>국가 및 항해</th>
                  <td>{itinerarySummaryText}</td>
                </tr>
                <tr>
                  <th>투숙자 수</th>
                  <td>
                    {
                      PASSENGER_TYPE_ORDER
                        .map(type => ({ type, count: passengerCountSummary[type] || 0 }))
                        .filter(item => item.count > 0)
                        .map(item => `${passengerLabels[item.type] || item.type} ${item.count}`)
                        .join(', ')
                      || '-'
                    }
                  </td>
                </tr>
              </>:
                <tr>
                  <th>국가 및 항해</th>
                  <td>{itinerarySummaryText}</td>
                  <th>투숙자 수</th>
                  <td>
                    {
                      PASSENGER_TYPE_ORDER
                        .map(type => ({ type, count: passengerCountSummary[type] || 0 }))
                        .filter(item => item.count > 0)
                        .map(item => `${passengerLabels[item.type] || item.type} ${item.count}`)
                        .join(', ')
                    }
                  </td>
                </tr>
              }

              {isTablet ? (
                <>
                  <tr>
                    <th>여행 기간</th>
                    <td>{sailNights}박 {sailNights + 1}일</td>
                  </tr>
                  <tr>
                    {reservationType === true ? (
                      <>
                      <th>결제 금액</th>
                      <td className="point-blue">
                          {formatCurrency(paidTotalDisplay, paymentCurrency)}
                      </td>
                    </>
                  ) : (
                      <>
                        <th>예상 금액</th>
                        <td className="point-blue">
                          {formatCurrency(totalAmountDisplay, paymentCurrency)}
                        </td>
                      </>
                    )}
                  </tr>
                </>
              ) : (
                <tr>
                  <th>여행 기간</th>
                  <td>{sailNights}박 {sailNights + 1}일</td>
                  {reservationType === true ? (
                    <>
                      <th>결제 금액</th>
                      <td className="point-blue">
                        {formatCurrency(paidTotalDisplay, paymentCurrency)}
                      </td>
                    </>
                  ) : (
                    <>
                    <th>예상 금액</th>
                    <td className="point-blue">
                      {formatCurrency(totalAmountDisplay, paymentCurrency)}
                    </td>
                    </>
                  )}
                </tr>
              )}

              <tr>
                <th>일정</th>
                <td colSpan={3} className='schedule'>
                  <div className='date-info'>
                    <div className='leave'>
                      <span className='point-blue'>출발</span>
                      <span>{departureLabel || '출발 정보 없음'}</span>
                    </div>
                    
                    <div className='arrive'>
                      <span className='point-blue'>도착</span>
                      <span>{arrivalLabel || '도착 정보 없음'}</span>
                    </div>
                  </div>

                  <ul className='date-detail-info'>
                    {sortedItinerary.map((item, idx) => {
                        // 날짜 포맷 (YYYY-MM-DD → YYYY년 M월 D일(요일))
                        const formattedDate = formatDateWithWeekday(item.arrivedate);

                        // 도착/출발 시간 포맷 (00:00:00이면 제외)
                        const arriveTime = formatTime(item.arrivetime);
                        const departTime = formatTime(item.departtime);

                        const portInfo = getPortInfo(item) || '정보 없음';

                        return (
                          <li key={idx}>
                            <span className='point-primary'>{item.day}일차</span>
                            <p>
                              {formattedDate} / {portInfo}
                              {arriveTime && ` / 도착 ${arriveTime}`}
                              {departTime && ` / 출발 ${departTime}`}
                              {item.extrainfo && ` / ${item.extrainfo}`}
                            </p>
                          </li>
                        );
                      })}
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 결제 요약 */}
        <div className="table-wrapper">
          <div className='wrapper-title'>
            <h2>결제 요약</h2>
          </div>
          <div className="payment-summary-box">
            <PromotionDiscountRows
              paymentSummary={apiPaymentSummary}
              promotion={paymentDataSource?.applied_promotion || bookingResult?.applied_promotion}
              formatCurrency={(value) => formatCurrency(value, paymentCurrency)}
            />
            <div className="payment-total-line">
              <span className="label">총 합계</span>
              <span className="amount">{formatCurrency(totalAmountDisplay, paymentCurrency)}</span>
            </div>
            {Array.isArray(onboardCreditSummary?.items) && onboardCreditSummary.items.length > 0 && (
              <div className='onboard-credit-summary'>
                <div className='onboard-credit-head'>
                  <span className='onboard-credit-title'>{onboardCreditSummary?.title || '특별 혜택'}</span>
                  <span className='onboard-credit-note'>{onboardCreditSummary?.note || '선내에서 사용 가능한 무료 리워드 크레딧'}</span>
                </div>
                {onboardCreditSummary.items.map((item, idx) => (
                  <div key={`onboard-credit-${idx}`} className='onboard-credit-row'>
                    <span className='onboard-credit-description'>{String(item?.description || 'On-Board Credit').toUpperCase()}</span>
                    <span className='onboard-credit-amount'>{item?.amountLabel || formatCurrency(item?.amount ?? 0, paymentCurrency)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="payment-breakdown">
              <div className="payment-line">
                <span className="label">예약금</span>
                <span className="amount">{formatCurrency(depositAmountDisplay, paymentCurrency)}</span>
                <span className="status">{depositStatusLabel}</span>
              </div>
              <div className="payment-line">
                <span className="label">잔금</span>
                <span className="amount">{formatCurrency(finalAmountDisplay, paymentCurrency)}</span>
                <span className="status">{finalStatusLabel}</span>
              </div>
            </div>
          </div>
        </div>

        {visiblePaymentReceiptsForView.length > 0 && (
          <div className="table-wrapper">
            <div className='wrapper-title'>
              <h2>결제 내역</h2>
            </div>
            <table className="payment-history-table">
              <thead>
                <tr>
                  <th>결제일</th>
                  <th>결제금액</th>
                  <th>항목</th>
                  <th>결제수단</th>
                </tr>
              </thead>
              <tbody>
                {visiblePaymentReceiptsForView.map((receipt, idx) => {
                  const paidDate = receipt?.paid_date || (receipt?.paid_at ? String(receipt.paid_at).slice(0, 10) : '');
                  const amountVal = parseAmount(receipt?.payment_amount_usd_adjusted ?? receipt?.payment_amount_usd);
                  const paymentType = String(receipt?.payment_type || '').toUpperCase();
                  const itemLabel = paymentType === 'D'
                    ? '예약금'
                    : paymentType === 'F'
                      ? '잔금'
                      : paymentType === 'T'
                        ? '전액'
                        : paymentType === 'R'
                          ? '환불'
                          : '-';
                  const cardType = receipt?.card_type || '';
                  const cardMasked = receipt?.card_number_masked || '';
                  const cardLabel = cardType && cardType !== 'UNKNOWN'
                    ? [cardType, cardMasked].filter(Boolean).join(' ')
                    : '-';
                  return (
                    <tr key={`receipt-${idx}`}>
                      <td>{paidDate || '-'}</td>
                      <td>{amountVal !== null ? formatCurrency(amountVal, paymentCurrency) : '-'}</td>
                      <td>{itemLabel}</td>
                      <td>{cardLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 승객 및 요금 내역 테이블 */}
        <div className="table-wrapper">
          <div className='wrapper-title'>
            <h2>승객 및 요금 내역</h2>
          </div>

          <table className="passenger-info-table">
            {/* <tbody>
              <tr>
                <th>{passengerLabels.adults} 1</th>
                <td>
                  <div className='info-contents'>
                    <div className='contents-value'><span className='point-primary'>이름</span><span>Gildong, Hong</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>생년월일</span><span>1999년 9월 17일</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>성별</span><span>Male</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>휴대폰 번호</span><span>+82 10-1234-1234</span></div>
                  </div>

                  <ul>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>선실 요금:<s className='ml-6 mr-4 point-none'>$1359.00</s><span className='mr-6'>$660.00</span><span className='point-grey'>(CCF: $540.00 / NCCF: $120.00)</span></div></li>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>세금:<span className='ml-6'>$68.12</span></div></li>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>선상팁: <span className='ml-6'>$80.00</span></div></li>
                    <li><IndentIcon className='mr-2 icon indent point-none'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>총계<s className='ml-6 mr-4 point-none'>$1,507.12</s>$808.12</div></li>
                  </ul>

                  <span className='point-grey'>(EXTRA SAVINGS: $-38 / 50% OFF REF: $-661 / 싱글차지: $0)</span>
                </td>
              </tr>

              <tr>
                <th>{passengerLabels.adults} 2</th>
                <td>
                  <div className='info-contents'>
                    <div className='contents-value'><span className='point-primary'>이름</span><span>Gildong, Hong</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>생년월일</span><span>1999년 9월 17일</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>성별</span><span>Male</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>휴대폰 번호</span><span>+82 10-1234-1234</span></div>
                  </div>

                  <ul>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>선실 요금:<s className='ml-6 mr-4 point-none'>$1359.00</s><span className='mr-6'>$660.00</span><span className='point-grey'>(CCF: $540.00 / NCCF: $120.00)</span></div></li>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>세금:<span className='ml-6'>$68.12</span></div></li>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>선상팁: <span className='ml-6'>$80.00</span></div></li>
                    <li><IndentIcon className='mr-2 icon indent point-none'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>총계<s className='ml-6 mr-4 point-none'>$1,507.12</s>$808.12</div></li>
                  </ul>

                  <span className='point-grey'>(EXTRA SAVINGS: $-38 / 50% OFF REF: $-661 / 싱글차지: $0)</span>
                </td>
              </tr>

              <tr>
                <th>{passengerLabels.children} 1</th>
                <td>
                  <div className='info-contents'>
                    <div className='contents-value'><span className='point-primary'>이름</span><span>Gildong, Hong</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>생년월일</span><span>1999년 9월 17일</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>성별</span><span>Male</span></div>
                    <span className='divider'></span>
                    <div className='contents-value'><span className='point-primary'>휴대폰 번호</span><span>+82 10-1234-1234</span></div>
                  </div>

                  <ul>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>선실 요금:<s className='ml-6 mr-4 point-none'>$1359.00</s><span className='mr-6'>$660.00</span><span className='point-grey'>(CCF: $540.00 / NCCF: $120.00)</span></div></li>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>세금:<span className='ml-6'>$68.12</span></div></li>
                    <li><DotIcon className='mr-3 icon dot'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>선상팁: <span className='ml-6'>$80.00</span></div></li>
                    <li><IndentIcon className='mr-2 icon indent point-none'/><div style={{flex:'1',display:'flex',flexDirection:'row',flexWrap:'wrap'}}>총계<s className='ml-6 mr-4 point-none'>$1,507.12</s>$808.12</div></li>
                  </ul>

                  <span className='point-grey'>(EXTRA SAVINGS: $-38 / 50% OFF REF: $-661 / 싱글차지: $0)</span>
                </td>
              </tr>
            </tbody> */}

            <tbody>
              {(() => {
                const typeCounters = {};
                return passengers.map((pax, idx) => {
                  // 승객별 요금 상세를 렌더링하기 위해 승객 번호 기준으로 가격 배열을 정리한다.
                const paxNo = pax?.paxno || pax?.pax_no || pax?.guestno || (idx + 1);
                  const paxPriceItems = passengersFromApi.length > 0
                    ? buildPassengerPriceItemsFromDetail(pax)
                    : passengersPrice
                      .map(item => {
                        const priceObj = item.prices && item.prices.find(p => String(p.guestno) === String(paxNo));
                        if (!priceObj) return null;
                        return {
                          description: item.description,
                          category: item.category,
                          price: priceObj.price,
                          symbol: priceObj.currencysymbol || item.prices[0]?.currencysymbol || '$'
                        };
                      })
                      .filter(Boolean);

                // 양수 금액은 기본 요금, 음수는 할인 항목으로 분리
                const positiveItems = paxPriceItems.filter(i => i.price >= 0);
                const discountItems = paxPriceItems.filter(i => i.price < 0);

                // 총 요금과 할인 이후 금액을 계산한다.
                const totalBeforeDiscount = positiveItems.reduce((s, i) => s + i.price, 0);
                const discountSum = discountItems.reduce((s, i) => s + i.price, 0); // 음수 합
                  const totalAfterDiscount = totalBeforeDiscount + discountSum;

                  // 화면 표시용 라벨과 포맷팅
                const rawType = pax.paxtype || pax.pax_type || pax.type || '';
                const typeKey = rawType.toLowerCase();
                typeCounters[typeKey] = (typeCounters[typeKey] || 0) + 1;
                const displayIndex = typeCounters[typeKey];
                  const typeLabelText = passengerLabels[mapPaxTypeToCategory(rawType)] || passengerLabels.children;
                const typeLabel = `${typeLabelText} ${displayIndex}`;
                const gender = normalizePassengerGender(pax.gender || pax.title || '');
                const dobFormatted = pax.dob
                  ? new Date(pax.dob).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
                  : '';
                const passengerPhone = formatPhoneDisplay(
                  pax.phone || pax.telephone || pax.mobile || pax.cell,
                  primaryPhoneRaw
                );

                  return (
                    <tr key={pax.paxno}>
                      <th>{typeLabel}</th>
                      <td>
                        <div className='info-contents'>
                        <div className='contents-value'>
                          <PassengerEnglishName lastName={pax.lastname} firstName={pax.firstname} />
                        </div>
                        <span className='divider'></span>

                        <div className='contents-value'>
                          <span className='point-primary'>생년월일</span>
                          <span>{dobFormatted}</span>
                        </div>
                        <span className='divider'></span>

                        <div className='contents-value'>
                          <span className='point-primary'>성별</span>
                          <span>{gender}</span>
                        </div>
                        <span className='divider'></span>

                        <div className='contents-value'>
                          <span className='point-primary'>휴대폰 번호</span>
                          <span>{passengerPhone || primaryPhone}</span>
                        </div>
                      </div>

                      <ul>
                        {/* 양수 금액 항목만 상세 리스트에 출력한다. */}
                        {positiveItems.map((it, i) => (
                          <li key={i}>
                            <DotIcon className='mr-3 icon dot' />
                            <div style={{ flex: '1', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                              {it.description}:
                              <span className='ml-6'>
                                {it.symbol}{it.price.toFixed(2)}
                              </span>
                            </div>
                          </li>
                        ))}

                        {/* 총계: 할인 전 금액과 할인 적용 후 금액을 동시에 보여준다. */}
                        <li>
                          <IndentIcon className='mr-2 icon indent point-none' />
                          <div style={{ flex: '1', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                            총계
                            <s className='ml-6 mr-4 point-none'>
                              {positiveItems.length > 0 ? `${positiveItems[0].symbol}${totalBeforeDiscount.toFixed(2)}` : `$0.00`}
                            </s>
                            {positiveItems.length > 0 ? `${positiveItems[0].symbol}${totalAfterDiscount.toFixed(2)}` : `$0.00`}
                          </div>
                        </li>
                      </ul>

                      {/* 할인 항목(음수)은 별도로 회색 텍스트로 표시한다. */}
                      <span className='point-grey'>
                        {discountItems.length > 0 && discountItems.map((d, i) => (
                          <span key={i} className='mr-4'>
                            {d.description.toUpperCase()}: {d.symbol}{Math.abs(d.price).toFixed(2)}
                          </span>
                        ))}
                      </span>
                    </td>
                  </tr>
                );
                });
              })()}
            </tbody>
          </table>
        </div>

        {/* 주의사항 */}
        <div className='table-wrapper'>
          <div className='wrapper-title'>
            <h2>주의사항</h2>
          </div>

          {renderCautionItems(cautionItems)}
        </div>      
        {/* 특별 약관 */}
        <div className='table-wrapper'>
          <div className='wrapper-title'>
            <h2>특별 약관</h2>
          </div>

          {specialTerms.description && (
            <p className='terms-caution point-secondary mb-16'>{specialTerms.description}</p>
          )}

          {renderTermItems(specialTerms.items || [], 'terms-contents mb-20 point-primary')}

        </div>    

        {/* 취소료 규정 */}
        <div className='table-wrapper'>
          <div className='wrapper-title'>
            <h2>취소료 규정</h2>
          </div>

          {renderTermItems(cancelTerms.items, 'cancle-fee point-secondary mb-16')}

          {cancelTerms.extra?.type === 'notes' && (
            <div className='point-grey' style={{ fontSize: '14px', fontWeight: '400' }}>
              {cancelTerms.extra.notes.map((note, index) => (
                <p key={index}>{note}</p>
              ))}
            </div>
          )}
        </div>    

        {/* 홈 & 내역 버튼 */}
        <div className='depth-5-btn-wrapper point-secondary'>
          <button type='button' className='btn' onClick={handleGoHome}>홈으로</button>
          <button type='button' className='btn' onClick={handleGoMypage}>예약 내역 보기</button>
        </div>

      </div>
    </>
  );
}
